import { cn } from '@/lib/utils'

export type OfficeApp = 'word' | 'excel' | 'powerpoint' | 'outlook'

const APPS: Record<OfficeApp, { letter: string; dark: string; light: string; accent: string }> = {
  word: { letter: 'W', dark: '#185abd', light: '#d3e3fb', accent: '#41a5ee' },
  excel: { letter: 'X', dark: '#107c41', light: '#cdebd9', accent: '#33c481' },
  powerpoint: { letter: 'P', dark: '#c43e1c', light: '#fbdcd2', accent: '#ff8f6b' },
  outlook: { letter: 'O', dark: '#0a64ad', light: '#cfe4f7', accent: '#28a8ea' },
}

/**
 * Office-style app mark drawn in-house: the app's lettered badge over a
 * document showing that app's content (text lines, grid, slide, envelope).
 * Recognisable as Word/Excel/PowerPoint/Outlook without reproducing
 * Microsoft's logo artwork. Decorative only.
 */
export function OfficeAppIcon({ app, className }: { app: OfficeApp; className?: string }) {
  const { letter, dark, light, accent } = APPS[app]
  return (
    <svg viewBox="0 0 48 48" aria-hidden className={cn('h-12 w-12', className)}>
      <rect x="15" y="6" width="29" height="36" fill={light} />
      {app === 'word' &&
        [14, 20, 26, 32].map((y) => <rect key={y} x="28" y={y} width="12" height="2.5" fill={accent} />)}
      {app === 'excel' && (
        <g fill="none" stroke={accent} strokeWidth="1.6">
          <rect x="28" y="12" width="12" height="24" />
          <line x1="34" y1="12" x2="34" y2="36" />
          {[18, 24, 30].map((y) => (
            <line key={y} x1="28" y1={y} x2="40" y2={y} />
          ))}
        </g>
      )}
      {app === 'powerpoint' && (
        <>
          <rect x="28" y="14" width="12" height="9" fill={accent} />
          <rect x="28" y="27" width="12" height="2.5" fill={accent} />
          <rect x="28" y="32" width="8" height="2.5" fill={accent} />
        </>
      )}
      {app === 'outlook' && (
        <g fill="none" stroke={accent} strokeWidth="1.8" strokeLinejoin="round">
          <rect x="27" y="16" width="14" height="14" />
          <polyline points="27,16 34,24 41,16" />
        </g>
      )}
      <rect x="4" y="13" width="22" height="22" fill={dark} />
      <text
        x="15"
        y="29.5"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="700"
        fontSize="15"
        fill="#fff"
      >
        {letter}
      </text>
    </svg>
  )
}

export default OfficeAppIcon
